import * as React from 'react'
import Loading from '@/components/Loading'
import TitleWithSubAction from '@/components/TitleWithSubAction'
import { getResizeUrl } from '@/utils/getResizeUrl'
import { MagicTemplateTile } from './MagicTemplateTile'

/** One selectable template style, flattened out of a category's `data` entry. */
export interface MagicTemplateStyle {
  name: string
  image: string
  prompt: string
  styleId: number
  aspectRatio: number
  quality: unknown
  model: unknown
  categoryId: number
}

export interface MagicTemplateCategoryData {
  id: number
  title: string
  aspect_ratio: number
  data: any[]
}

/**
 * Flattens one category's raw template entry into a `MagicTemplateStyle`.
 * Shared by `MagicTemplateTile` (click-to-select) and any caller that needs to
 * pick a style programmatically (e.g. auto-selecting the first one on open).
 */
export function buildMagicTemplateStyle(
  item: { id: number; title: string; prompt: string; quality: unknown; model_version: unknown; assets: { file: string }[] },
  categoryId: number,
  aspectRatio: number,
): MagicTemplateStyle {
  return {
    name: item.title,
    image: getResizeUrl({ size: '360x0', url: item.assets[1].file }).toString(),
    prompt: item.prompt,
    styleId: item.id,
    aspectRatio,
    quality: item.quality,
    model: item.model_version,
    categoryId,
  }
}

export interface MagicTemplatesContentProps {
  /** `null`/empty renders the loading state. */
  categories: MagicTemplateCategoryData[] | null
  onSelectStyle: (style: MagicTemplateStyle) => void
  onSeeAll: (category: MagicTemplateCategoryData) => void
  /** Locks tiles while a generation is running. */
  isLoading?: boolean
  /** Style currently generating — drives the tile's spinner/try-again state. */
  styleIdSelected?: number | null
  hasPremium?: boolean
  /** How many tiles each category row previews before "See all". */
  previewCount?: number
  /** Forwarded to `WrapAnimationImage`. Off in Batch's read-only style browser. */
  showTryAgainOnActive?: boolean
  /**
   * Skips each category's title/"See all" row. For a single-category detail
   * view (pass one category and `previewCount={category.data.length}`) where
   * the title already lives in the panel's own header.
   */
  hideCategoryHeader?: boolean
}

/**
 * Presentational AI-templates browser: one `TitleWithSubAction` section per
 * category with a preview row of tiles. Extracted from `MagicTemplatesTool` so
 * surfaces without the pixel-manipulation editor (Batch Studio) can render the
 * same grid — every side effect is reported through the callbacks.
 */
export function MagicTemplatesContent({
  categories,
  onSelectStyle,
  onSeeAll,
  showTryAgainOnActive = true,
  isLoading = false,
  styleIdSelected = null,
  hasPremium = false,
  previewCount = 3,
  hideCategoryHeader = false,
}: MagicTemplatesContentProps) {
  if (!categories || categories.length === 0) {
    // height:100% (not a fixed px guess) centers against the scroll body's
    // real, resolved height — which differs by caller (Batch's footer grows for
    // its credits caption, the editor's doesn't) — without overlapping either
    // one, unlike the old absolute-positioned overlay this replaced.
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <Loading />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', paddingBottom: '24px' }}>
      {categories.map((category) => {
        const aspectRatio = category.aspect_ratio

        return (
          <div key={category.id} style={{ display: 'flex', flexDirection: 'column' }}>
            {hideCategoryHeader ? null : (
              <TitleWithSubAction
                title={category.title}
                onSubActionClick={() => onSeeAll(category)}
                disabled={isLoading}
              />
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {category.data.slice(0, previewCount).map((item) => (
                <MagicTemplateTile
                  key={item.id}
                  item={item}
                  categoryId={category.id}
                  aspectRatio={aspectRatio}
                  onSelectStyle={onSelectStyle}
                  isLoading={isLoading}
                  hasPremium={hasPremium}
                  styleIdSelected={styleIdSelected}
                  showTryAgainOnActive={showTryAgainOnActive}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default MagicTemplatesContent
